import {AlertTriangle, X} from 'lucide-react'
import {useEffect, useRef} from 'react'

interface ConfirmDialogProps {
  isOpen: boolean
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'danger' | 'warning'
  onConfirm: () => void
  onCancel: () => void
}

export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  variant = 'danger',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    if (isOpen) window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [isOpen, onCancel])

  if (!isOpen) return null

  const isDanger = variant === 'danger'

  return (
    <div
      ref={overlayRef}
      className='fixed inset-0 z-[60] flex items-center justify-center px-4 animate-fade-in'
      onClick={(e) => {
        if (e.target === overlayRef.current) onCancel()
      }}
      role='alertdialog'
      aria-modal='true'
      aria-label={title}
    >
      <div className='fixed inset-0 bg-slate-900/50 backdrop-blur-sm' />
      <div className='devops-surface relative w-full max-w-sm animate-scale-in p-6'>
        <button
          onClick={onCancel}
          className='absolute top-4 right-4 p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors'
          aria-label='Fechar'
        >
          <X size={18} />
        </button>

        <div className='flex items-start gap-4 mb-4'>
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
              isDanger ? 'bg-red-50' : 'bg-amber-50'
            }`}
          >
            <AlertTriangle size={20} className={isDanger ? 'text-red-500' : 'text-amber-500'} />
          </div>
          <div>
            <h3 className='text-base font-semibold text-slate-800'>{title}</h3>
            <p className='text-sm text-slate-500 mt-1 leading-relaxed'>{message}</p>
          </div>
        </div>

        <div className='flex gap-3 mt-6'>
          <button
            onClick={onCancel}
            className='flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors'
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white transition-colors ${
              isDanger ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
